// =============================================================================
// workflowReportUpload.ts — Upload / replace / remove a user's own measurement
// or estimate report, plus DealFile-first document resolution. Web twin of
// mobile's NotesToHankChat/workflowReportUpload.ts + utils/dealDocuments.ts —
// same pipeline, same rules, so web and mobile stay in lockstep (GG-3433).
//
// Identity model (the GG-3402 contract):
//   - The file is a DealFile record: id = client-minted uuidv4 (stable),
//     url = the CURRENT S3 key. Every write lands at a NEW timestamped key,
//     so CloudFront/browser caches can never serve a stale file.
//   - The ORDER references it via dealFileId and dual-writes pdfUrl with the
//     same key for legacy readers (pdfUrl must stay a relative public-area
//     S3 key — both web and mobile pass it raw into Storage.get).
//   - upload  = pick file -> S3 -> createOrder(source=USER_UPLOAD)
//               -> createDealFile -> READY + dealFileId (+pdfUrl)
//   - replace = pick file -> S3 (new key) -> updateDealFile url (SAME id)
//               -> order stays READY; no CANCELLED window, so the chat
//               agent's workflow card never flickers.
//   - remove  = active USER_UPLOAD order -> updateStatus CANCELLED
// Replace/remove apply only to USER_UPLOAD orders (purchased reports are
// view-only here; the contractor Xactimate flow is a separate surface).
// =============================================================================

import { API, Auth, graphqlOperation, Storage } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { createDealFile, updateDealFile } from '../../graphql/mutations';
import { getDealFile } from '../../graphql/queries';

const maintainMeasurementOrder = /* GraphQL */ `
  mutation MaintainMeasurementOrder($request: AWSJSON!) {
    maintainMeasurementOrder(request: $request)
  }
`;

const maintainEstimateOrder = /* GraphQL */ `
  mutation MaintainEstimateOrder($request: AWSJSON!) {
    maintainEstimateOrder(request: $request)
  }
`;

export type ReportKind = 'measurement' | 'estimate';

export const SOURCE_USER_UPLOAD = 'USER_UPLOAD';

// Statuses that mean an order is no longer live. Mirrors mobile + the chat
// agent's gating: CANCELLED/FAILED rows count as "no report".
export const INACTIVE_STATUSES = ['FAILED', 'CANCELLED'];

const ALLOWED_EXTENSIONS = ['pdf', 'esx', 'xml'];

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  xml: 'application/xml',
  esx: 'application/octet-stream',
};

const REPORT_TYPE: Record<ReportKind, string> = {
  measurement: 'MEASUREMENT_REPORT',
  estimate: 'XACTIMATE_ESTIMATE',
};

const CATEGORY: Record<ReportKind, string> = {
  measurement: 'measurement_report',
  estimate: 'estimate',
};

const LABEL: Record<ReportKind, string> = {
  measurement: 'measurement report',
  estimate: 'estimate',
};

interface KindConfig {
  mutation: string;
  responseField: string;
  createOp: string;
  updateOp: string;
  listOp: string;
  orderKey: string;
  orderListKey: string;
}

const CONFIG: Record<ReportKind, KindConfig> = {
  measurement: {
    mutation: maintainMeasurementOrder,
    responseField: 'maintainMeasurementOrder',
    createOp: 'createMeasurementOrder',
    updateOp: 'updateMeasurementOrderStatus',
    listOp: 'getMeasurementOrdersByDeal',
    orderKey: 'measurementOrder',
    orderListKey: 'measurementOrders',
  },
  estimate: {
    mutation: maintainEstimateOrder,
    responseField: 'maintainEstimateOrder',
    createOp: 'createEstimateOrder',
    updateOp: 'updateEstimateOrderStatus',
    listOp: 'getEstimateOrdersByDeal',
    orderKey: 'estimateOrder',
    orderListKey: 'estimateOrders',
  },
};

export interface WorkflowReportResult {
  success: boolean;
  /** True when the user closed the file dialog — not an error to surface. */
  canceled?: boolean;
  error?: string;
  dealFileId?: string | null;
  s3Key?: string;
}

// The order Lambdas return an AWSJSON string `{ success, data, error }`,
// sometimes double-encoded. Unwrap until it's an object.
function parseLambdaResponse(raw: any): any {
  let parsed = raw;
  while (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }
  return parsed;
}

async function callOrderOp(
  kind: ReportKind,
  payload: Record<string, any>
): Promise<any> {
  const cfg = CONFIG[kind];
  const result: any = await API.graphql(
    graphqlOperation(cfg.mutation, { request: JSON.stringify(payload) })
  );
  return parseLambdaResponse(result?.data?.[cfg.responseField]);
}

/** The deal's newest ACTIVE order (CANCELLED/FAILED skipped), or null. */
export async function getActiveOrder(
  kind: ReportKind,
  dealId: string
): Promise<any | null> {
  const listed = await callOrderOp(kind, {
    operation: CONFIG[kind].listOp,
    dealID: dealId,
  });
  const orders: any[] = listed?.data?.[CONFIG[kind].orderListKey] || [];
  return orders.find((o) => o && !INACTIVE_STATUSES.includes(o.status)) || null;
}

interface PickedFile {
  file: File;
  name: string;
  ext: string;
  mimeType: string;
}

/** Browser file dialog + extension validation (web stand-in for mobile's
 *  document picker). Resolves canceled=true if the user closes the dialog. */
function pickReportFile(): Promise<{
  picked?: PickedFile;
  canceled?: boolean;
  error?: string;
}> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) {
        resolve({ canceled: true });
        return;
      }
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        resolve({
          error: `Please choose a ${ALLOWED_EXTENSIONS.map((e) =>
            e.toUpperCase()
          ).join(', ')} file.`,
        });
        return;
      }
      resolve({
        picked: {
          file,
          name: file.name,
          ext,
          mimeType: file.type || MIME_BY_EXT[ext] || 'application/octet-stream',
        },
      });
    };
    // Cancel detection: focus returns to the window without a change event.
    window.addEventListener(
      'focus',
      () => setTimeout(() => resolve({ canceled: true }), 500),
      { once: true }
    );
    input.click();
  });
}

/** Upload to a NEW timestamped key — never overwrites (new path = cache bust). */
async function putReportFile(
  kind: ReportKind,
  dealId: string,
  picked: PickedFile
): Promise<string | null> {
  try {
    const safeName = picked.name.replace(/[^\w.-]/g, '_');
    const s3Key = `reports/${dealId}/${kind}/${Date.now()}-${safeName}`;
    await Storage.put(s3Key, picked.file, {
      contentType: picked.mimeType,
      level: 'public',
    });
    return s3Key;
  } catch (err) {
    console.error(`[WorkflowUpload] S3 upload failed (${kind}):`, err);
    return null;
  }
}

/** DealFile record for an uploaded report — same shape mobile writes. The
 *  createDealFile mutation also fires onCreateDealFileByDealID so mobile
 *  viewers refresh live. Non-fatal: upload still succeeds via legacy pdfUrl. */
async function createReportDealFile(params: {
  kind: ReportKind;
  dealId: string;
  orgId: string;
  userId: string;
  orderId: string;
  s3Key: string;
  ext: string;
  fileName: string;
}): Promise<string | null> {
  try {
    const input: any = {
      id: uuidv4(),
      dealID: params.dealId,
      orgID: params.orgId,
      type: MIME_BY_EXT[params.ext] || 'application/octet-stream',
      category: CATEGORY[params.kind],
      reportType: REPORT_TYPE[params.kind],
      source: SOURCE_USER_UPLOAD,
      orderID: params.orderId,
      url: params.s3Key,
      description: `${LABEL[params.kind]} (uploaded: ${params.fileName})`,
      createdBy: params.userId,
      lastUpdatedBy: params.userId,
      fileTimestampEpoch: Date.now(),
      sequence: 0,
      tags: [CATEGORY[params.kind], SOURCE_USER_UPLOAD],
      isDeleted: false,
    };
    const res: any = await API.graphql(
      graphqlOperation(createDealFile, { input })
    );
    return res?.data?.createDealFile?.id || input.id;
  } catch (err) {
    console.error('[WorkflowUpload] createDealFile failed (non-fatal):', err);
    return null;
  }
}

/** Re-point an existing DealFile at a new key (replace-in-place). */
async function rekeyReportDealFile(
  dealFileId: string,
  s3Key: string,
  fileName: string,
  kind: ReportKind,
  userId: string
): Promise<boolean> {
  try {
    await API.graphql(
      graphqlOperation(updateDealFile, {
        input: {
          id: dealFileId,
          url: s3Key,
          description: `${LABEL[kind]} (uploaded: ${fileName})`,
          lastUpdatedBy: userId,
          fileTimestampEpoch: Date.now(),
        },
      })
    );
    return true;
  } catch (err) {
    console.error('[WorkflowUpload] updateDealFile (re-key) failed:', err);
    return false;
  }
}

export interface DealContext {
  dealId: string;
  /** Defaults to the signed-in user's org (global me), then the dealId prefix. */
  orgId?: string;
  /** Defaults to the signed-in user (global me). */
  userId?: string;
  dealName?: string;
  dealAddress?: string;
}

function resolveOrgId(ctx: DealContext): string {
  return (
    ctx.orgId ||
    (globalThis as any)?.me?.orgID ||
    (ctx.dealId.includes(':') ? ctx.dealId.split(':')[0] : '')
  );
}

function resolveUserId(ctx: DealContext): string {
  return ctx.userId || (globalThis as any)?.me?.id || '';
}

/** Pick a file, upload it, and record it as a USER_UPLOAD order in READY
 *  state with a DealFile record. */
export async function uploadWorkflowReport(
  kind: ReportKind,
  ctx: DealContext
): Promise<WorkflowReportResult> {
  if (!ctx?.dealId)
    return { success: false, error: 'Missing deal information.' };
  const cfg = CONFIG[kind];

  const { picked, canceled, error } = await pickReportFile();
  if (!picked) return { success: false, canceled, error };

  const s3Key = await putReportFile(kind, ctx.dealId, picked);
  if (!s3Key)
    return { success: false, error: 'Upload failed. Please try again.' };

  try {
    const created = await callOrderOp(kind, {
      operation: cfg.createOp,
      dealID: ctx.dealId,
      source: SOURCE_USER_UPLOAD,
      reportProvider: SOURCE_USER_UPLOAD,
      dealName: ctx.dealName || '',
      dealAddress: ctx.dealAddress || '',
    });
    const order = created?.data?.[cfg.orderKey];
    if (!created?.success || !order?.id) {
      console.error(
        `[WorkflowUpload] create order failed (${kind}):`,
        created?.error
      );
      return {
        success: false,
        error: `Could not save the ${LABEL[kind]}. Please try again.`,
      };
    }
    if (created?.data?.existing) {
      return {
        success: false,
        error: `A ${LABEL[kind]} already exists on this deal. Use Replace, or remove it first.`,
      };
    }

    const dealFileId = await createReportDealFile({
      kind,
      dealId: ctx.dealId,
      orgId: resolveOrgId(ctx),
      userId: resolveUserId(ctx),
      orderId: order.id,
      s3Key,
      ext: picked.ext,
      fileName: picked.name,
    });

    const updated = await callOrderOp(kind, {
      operation: cfg.updateOp,
      orderID: order.id,
      status: 'READY',
      pdfUrl: s3Key,
      ...(dealFileId ? { dealFileId } : {}),
    });
    if (!updated?.success) {
      console.error(
        `[WorkflowUpload] finalize failed (${kind}):`,
        updated?.error
      );
      return {
        success: false,
        error: `Could not finalize the ${LABEL[kind]}. Please try again.`,
      };
    }
    return { success: true, dealFileId, s3Key };
  } catch (err) {
    console.error(`[WorkflowUpload] upload threw (${kind}):`, err);
    return {
      success: false,
      error: `Could not save the ${LABEL[kind]}. Please try again.`,
    };
  }
}

/** Replace the file behind an existing USER_UPLOAD order: same DealFile id,
 *  NEW S3 key, order stays READY throughout (no CANCELLED window). Orders
 *  that predate DealFile records get one attached on first replace. */
export async function replaceWorkflowReport(
  kind: ReportKind,
  ctx: DealContext
): Promise<WorkflowReportResult> {
  if (!ctx?.dealId)
    return { success: false, error: 'Missing deal information.' };
  const cfg = CONFIG[kind];

  try {
    const target = await getActiveOrder(kind, ctx.dealId);
    if (!target?.id || target.source !== SOURCE_USER_UPLOAD) {
      return {
        success: false,
        error: `No uploaded ${LABEL[kind]} to replace.`,
      };
    }

    const { picked, canceled, error } = await pickReportFile();
    if (!picked) return { success: false, canceled, error };

    const s3Key = await putReportFile(kind, ctx.dealId, picked);
    if (!s3Key)
      return { success: false, error: 'Upload failed. Please try again.' };

    let dealFileId: string | null = target.dealFileId || null;
    if (dealFileId) {
      const ok = await rekeyReportDealFile(
        dealFileId,
        s3Key,
        picked.name,
        kind,
        resolveUserId(ctx)
      );
      if (!ok) dealFileId = null;
    } else {
      dealFileId = await createReportDealFile({
        kind,
        dealId: ctx.dealId,
        orgId: resolveOrgId(ctx),
        userId: resolveUserId(ctx),
        orderId: target.id,
        s3Key,
        ext: picked.ext,
        fileName: picked.name,
      });
    }

    const updated = await callOrderOp(kind, {
      operation: cfg.updateOp,
      orderID: target.id,
      status: 'READY',
      pdfUrl: s3Key,
      ...(dealFileId ? { dealFileId } : {}),
    });
    if (!updated?.success) {
      console.error(
        `[WorkflowUpload] replace finalize failed (${kind}):`,
        updated?.error
      );
      return {
        success: false,
        error: `Could not replace the ${LABEL[kind]}. Please try again.`,
      };
    }
    return { success: true, dealFileId, s3Key };
  } catch (err) {
    console.error(`[WorkflowUpload] replace threw (${kind}):`, err);
    return {
      success: false,
      error: `Could not replace the ${LABEL[kind]}. Please try again.`,
    };
  }
}

/** Remove a user-uploaded report by cancelling its order (no hard delete). */
export async function removeWorkflowReport(
  kind: ReportKind,
  dealId: string
): Promise<WorkflowReportResult> {
  if (!dealId) return { success: false, error: 'Missing deal information.' };
  const cfg = CONFIG[kind];
  try {
    const target = await getActiveOrder(kind, dealId);
    if (!target?.id || target.source !== SOURCE_USER_UPLOAD) {
      return { success: false, error: `No uploaded ${LABEL[kind]} to remove.` };
    }
    const updated = await callOrderOp(kind, {
      operation: cfg.updateOp,
      orderID: target.id,
      status: 'CANCELLED',
    });
    if (!updated?.success) {
      console.error(
        `[WorkflowUpload] cancel failed (${kind}):`,
        updated?.error
      );
      return {
        success: false,
        error: `Could not remove the ${LABEL[kind]}. Please try again.`,
      };
    }
    return { success: true };
  } catch (err) {
    console.error(`[WorkflowUpload] remove threw (${kind}):`, err);
    return {
      success: false,
      error: `Could not remove the ${LABEL[kind]}. Please try again.`,
    };
  }
}

// =============================================================================
// Document resolution — web twin of mobile's utils/dealDocuments.ts. The
// DealFile record holds the CURRENT key (files can be re-keyed on replace);
// the order's pdfUrl is the legacy fallback.
// =============================================================================

export interface DealDocumentRef {
  dealFileId?: string | null;
  s3Key?: string | null;
}

/** Resolve a deal document to a presigned URL (DealFile-first), or null. */
export async function resolveDealDocumentUrl(
  ref: DealDocumentRef
): Promise<string | null> {
  if (ref.dealFileId) {
    try {
      const res: any = await API.graphql(
        graphqlOperation(getDealFile, { id: ref.dealFileId })
      );
      const dealFile = res?.data?.getDealFile;
      if (dealFile?.url && !dealFile?.isDeleted) {
        return (await Storage.get(dealFile.url, { level: 'public' })) as string;
      }
    } catch (err) {
      console.warn(
        '[dealDocuments] DealFile lookup failed, falling back:',
        err
      );
    }
  }
  if (ref.s3Key) {
    try {
      return (await Storage.get(ref.s3Key, { level: 'public' })) as string;
    } catch (err) {
      console.error(
        '[dealDocuments] Storage.get failed for key:',
        ref.s3Key,
        err
      );
    }
  }
  return null;
}

export function hasDealDocument(ref: DealDocumentRef): boolean {
  return !!(ref.dealFileId || ref.s3Key);
}

/** External read-only users (the Xactimate/contractor team, paiv_admin_ro):
 *  they can UPLOAD reports (the order Lambdas allow any signed-in user) but
 *  must never see purchase options — orders charge the customer org. */
export async function isExternalReadOnlyUser(): Promise<boolean> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const groups: string[] =
      user?.signInUserSession?.accessToken?.payload?.['cognito:groups'] || [];
    return groups.includes('paiv_admin_ro');
  } catch {
    return false;
  }
}
